package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LoadErrorState
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.loadGuarded
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.PeopleSnapshot
import br.com.vvrefeicoes.portal.domain.model.unidadeKeyOf
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import br.com.vvrefeicoes.portal.domain.repository.PeopleRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

class PeopleController(
    private val repository: PeopleRepository,
    private val orgChartRepository: OrgChartRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
    private val loadError: LoadErrorState = LoadErrorState(),
) {
    var loading by mutableStateOf(true)
        private set

    var selection by mutableStateOf(PeriodSelection.current(dateTimeProvider.currentPeriod()))
        private set

    var snapshot by mutableStateOf<PeopleSnapshot?>(null)
        private set

    private var started = false
    private var unitFilter: CompanyRef? = null
    private var managerFilter: String? = null
    private var directorFilter: String? = null
    private var authorizedUnits: List<CompanyRef> = emptyList()
    private var orgChartData: OrgChartData? = null

    fun init() {
        if (started) return
        started = true
        scope.launch {
            loadError.loadGuarded { load() }
            loading = false
        }
    }

    fun applyFilter(
        unit: CompanyRef?,
        manager: String?,
        director: String?,
        authorizedUnits: List<CompanyRef>,
    ) {
        if (unit == unitFilter && manager == managerFilter && director == directorFilter && authorizedUnits == this.authorizedUnits) return
        unitFilter = unit
        managerFilter = manager
        directorFilter = director
        this.authorizedUnits = authorizedUnits
        scope.launch { loadError.loadGuarded { load() } }
    }

    fun applyPeriod(newSelection: PeriodSelection) {
        if (newSelection == selection) return
        selection = newSelection
        scope.launch { loadError.loadGuarded { load() } }
    }

    private suspend fun load() {
        val data =
            orgChartData ?: orgChartRepository
                .loadInitial()
                .currentVersion.data
                .also { orgChartData = it }
        val unitId = unitFilter?.let { data.unidadeKeyOf(it) }
        snapshot = repository.load(selection, unitId, managerFilter, directorFilter)
    }
}

@Composable
fun rememberPeopleController(): PeopleController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        PeopleController(
            repository = appGraph.peopleRepository,
            orgChartRepository = appGraph.orgChartRepository,
            scope = scope,
            dateTimeProvider = appGraph.dateTimeProvider,
            loadError = appGraph.loadError,
        )
    }
}
